import axios from "axios";
import { logError, logWarn } from "../../shared/logger/frontendLogger.js";

// Base da API:
// - DEV: respeita VITE_API_URL (fallback localhost)
// - PROD: respeita VITE_API_URL; se não existir, usa heurística do domínio público
const envBase = String(import.meta.env.VITE_API_URL || "").trim();
const fallbackLocal = "http://localhost:4000";
const isProdBuild = Boolean(import.meta.env.PROD);

// Heurística só para produção (evita divergência local -> prod por "origin").
const browserOrigin =
  typeof window !== "undefined" ? window.location.origin : "";
const inferredProdBase =
  isProdBuild && browserOrigin.includes("gruposimempneo.com")
    ? "https://api.gruposimempneo.com"
    : null;

// prioridade: env > inferredProdBase > localhost
const rawBase = envBase || inferredProdBase || fallbackLocal;

// garante que sempre termina com /api
const baseURL = rawBase.endsWith("/api") ? rawBase : `${rawBase}/api`;

const api = axios.create({
  baseURL,
  timeout: 10000,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// Normaliza campos "date-only" vindos como ISO com hora (ex.: 2025-10-30T00:00:00Z)
function normalizeDateOnlyFields(obj) {
  if (!obj || typeof obj !== "object") return obj;
  const keysToNormalize = new Set([
    "inicio_previsto",
    "fim_previsto",
    "inicio_real",
    "fim_real",
    "mural_inicio_previsto",
    "mural_fim_previsto",
    "data_inicio",
    "data_fim",
    "start",
    "end",
  ]);
  const stack = [obj];
  while (stack.length) {
    const cur = stack.pop();
    if (!cur || typeof cur !== "object") continue;
    if (Array.isArray(cur)) {
      for (const it of cur) stack.push(it);
      continue;
    }
    for (const k of Object.keys(cur)) {
      const v = cur[k];
      if (v && typeof v === "object") {
        stack.push(v);
        continue;
      }
      if (typeof v === "string" && keysToNormalize.has(k)) {
        if (/^\d{4}-\d{2}-\d{2}(?:T.*)?$/.test(v)) {
          cur[k] = v.slice(0, 10);
        }
      }
    }
  }
  return obj;
}

api.interceptors.response.use(
  (response) => {
    try {
      normalizeDateOnlyFields(response.data);
    } catch (err) {
      logWarn("normalize_date_fields_failed", {
        url: response?.config?.url,
        error: err?.message || String(err),
      });
    }
    return response;
  },
  (error) => {
    const errorPayload = {
      url: error?.config?.url,
      method: error?.config?.method,
      status: error?.response?.status,
      requestId: error?.response?.data?.requestId,
      code: error?.response?.data?.code,
      message: error?.message,
    };
    logError("API_ERROR", errorPayload);

    if (error.response?.status === 401) {
      const isOnLogin =
        typeof window !== "undefined" &&
        window.location.pathname.startsWith("/login");
      const isAuthCall = (error?.config?.url || "").includes("/auth/login");
      if (!isOnLogin && !isAuthCall) {
        // opcional: limpar storage
      }
    }
    return Promise.reject(error);
  },
);

export default api;
